import React, { useState, useEffect, useMemo } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { eventService, PendingValidationEvent } from '@/services/eventService';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import SubscriptionRequiredModal from '@/components/payment/SubscriptionRequiredModal';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  AlertCircle, ArrowLeft, CalendarIcon, Search, Filter, Check, User, Users, Clock, Loader2, ChevronRight, ChevronDown
} from 'lucide-react';
import { format, isWithinInterval, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { cn } from '@/lib/utils';
import { DateRange as RDPDateRange } from 'react-day-picker';

type DateRange = Partial<RDPDateRange>;
type FilterPeriod = 'all' | 'currentMonth' | 'currentYear' | 'custom';

const InvoicesPendingValidation = () => {
  const { t, locale } = useLanguage();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState<any | undefined>(undefined);
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('all');
  const [dateRange, setDateRange] = useState<DateRange>({});
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(new Set());
  const [validatingIds, setValidatingIds] = useState<Set<string>>(new Set());
  const [showValidateAllConfirm, setShowValidateAllConfirm] = useState(false);

  const hasAccess = user?.is_subscribed || user?.is_trialing;

  useEffect(() => {
    if (!hasAccess) {
      setShowSubscriptionModal(true);
    }
  }, [hasAccess]);

  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['pendingValidationEvents', token],
    queryFn: () => eventService.getPendingValidationEvents(token),
    enabled: !!token && hasAccess,
  });

  const { data: students = [], isLoading: studentsLoading } = useQuery({
    queryKey: ['students', token],
    queryFn: () => eventService.getStudents(user, token),
    enabled: !!token && hasAccess,
  });

  useEffect(() => {
    if (filterPeriod === 'all') {
      if (events.length > 0) {
        const dates = events.map((e) => new Date(e.date));
        const minDate = new Date(Math.min(...dates.map((d) => d.getTime())));
        const maxDate = new Date(Math.max(...dates.map((d) => d.getTime())));
        setDateRange({ from: minDate, to: maxDate });
      }
    } else if (filterPeriod === 'currentMonth') {
      const now = new Date();
      setDateRange({ from: startOfMonth(now), to: endOfMonth(now) });
    } else if (filterPeriod === 'currentYear') {
      const now = new Date();
      setDateRange({ from: startOfYear(now), to: endOfYear(now) });
    }
  }, [filterPeriod, events]);

  const filteredEvents = useMemo(() => {
    if (eventsLoading || !events) return [];

    let filtered = [...events];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (event) =>
          event.studentName.toLowerCase().includes(term) ||
          event.category.toLowerCase().includes(term) ||
          (event.title && event.title.toLowerCase().includes(term))
      );
    }

    if (selectedStudent) {
      const fullName = `${selectedStudent.firstname} ${selectedStudent.lastname}`.toLowerCase();
      filtered = filtered.filter((event) => {
        const studentName = event.studentName.toLowerCase();
        return (
          studentName.includes(fullName) ||
          studentName.includes(selectedStudent.firstname.toLowerCase()) &&
          studentName.includes(selectedStudent.lastname.toLowerCase())
        );
      });
    }

    if (dateRange.from && dateRange.to) {
      const start = new Date(dateRange.from);
      start.setHours(0, 0, 0, 0);
      const end = new Date(dateRange.to);
      end.setHours(23, 59, 59, 999);

      filtered = filtered.filter((event) => {
        const eventDate = new Date(event.date);
        return isWithinInterval(eventDate, { start, end });
      });
    }

    // Sort chronologically by date, newest first
    return filtered.sort((a, b) => {
      return new Date(b.date).getTime() - new Date(a.date).getTime();
    });
  }, [events, eventsLoading, searchTerm, selectedStudent, dateRange]);

  // Group rows sharing the same event.id (same lesson with multiple students).
  // Validating one row validates all rows in the group.
  const groupedEvents = useMemo(() => {
    const map = new Map<string, {
      id: string;
      date: string;
      category: string;
      categoryColor?: string;
      duration: number;
      title?: string;
      allDay?: boolean;
      students: { studentId: string; studentName: string; price: number }[];
      totalPrice: number;
    }>();
    for (const e of filteredEvents) {
      const existing = map.get(e.id);
      if (existing) {
        existing.students.push({ studentId: e.studentId, studentName: e.studentName, price: e.price });
        existing.totalPrice += e.price || 0;
      } else {
        map.set(e.id, {
          id: e.id,
          date: e.date,
          category: e.category,
          categoryColor: e.categoryColor,
          duration: e.duration,
          title: e.title,
          allDay: (e as any).allDay,
          students: [{ studentId: e.studentId, studentName: e.studentName, price: e.price }],
          totalPrice: e.price || 0,
        });
      }
    }
    return Array.from(map.values());
  }, [filteredEvents]);

  const resetFilters = () => {
    setSelectedStudent(undefined);
    setSearchTerm('');
    setFilterPeriod('all');
    setDateRange({});
  };

  const translateStr = (key: string): string => {
    const translated = t(key);
    return Array.isArray(translated) ? translated.join(' ') : translated;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: user.currency_code || 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat(locale === 'fr' ? 'fr-FR' : 'en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateString));
  };

  const toggleEvent = (eventId: string) => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) {
        next.delete(eventId);
      } else {
        next.add(eventId);
      }
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedEventIds.size === groupedEvents.length) {
      setSelectedEventIds(new Set());
    } else {
      setSelectedEventIds(new Set(groupedEvents.map((e) => e.id)));
    }
  };

  const handleValidate = async (eventIds: string[]) => {
    if (!eventIds.length) return;

    setValidatingIds((prev) => {
      const next = new Set(prev);
      eventIds.forEach((id) => next.add(id));
      return next;
    });

    try {
      if (eventIds.length === 1) {
        await eventService.markEventAsCompleted(token, eventIds[0]);
      } else {
        await eventService.markEventsAsCompleted(token, eventIds);
      }
      toast({
        title: translateStr('invoices.eventsValidated'),
        description: translateStr('invoices.eventsValidatedDescription'),
      });
      setSelectedEventIds((prev) => {
        const next = new Set(prev);
        eventIds.forEach((id) => next.delete(id));
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['pendingValidationEvents', token] });
      queryClient.invalidateQueries({ queryKey: ['pendingBillings', token] });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: translateStr('common.error'),
        description: error instanceof Error ? error.message : translateStr('unknownError'),
      });
    } finally {
      setValidatingIds((prev) => {
        const next = new Set(prev);
        eventIds.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  const allSelected = groupedEvents.length > 0 && selectedEventIds.size === groupedEvents.length;

  const validateAllDateRange = useMemo(() => {
    if (groupedEvents.length === 0) return null;
    const times = groupedEvents.map((e) => new Date(e.date).getTime());
    return {
      from: new Date(Math.min(...times)),
      to: new Date(Math.max(...times)),
    };
  }, [groupedEvents]);

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <SubscriptionRequiredModal
        isOpen={showSubscriptionModal}
        onClose={() => {
          setShowSubscriptionModal(false);
          if (!hasAccess) {
            navigate('/invoices');
          }
        }}
      />

      <main className="flex-1 container py-8 mt-16">
        {hasAccess ? (
          <>
            <div>
              <Button variant="ghost" onClick={() => navigate('/invoices/billing')} className="mb-4 bg-gray-50 hover:bg-gray-100 dark:bg-gray-800 dark:hover:bg-gray-700">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {translateStr('common.back')}
              </Button>
            </div>

            <Card className="border-0 rounded-none shadow-none p-1 md:border md:rounded-xl md:shadow-sm md:p-0">
              <CardHeader className="p-0 md:p-4">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-1.5">
                  <div>
                    <CardTitle className="pb-2">{translateStr('invoices.pendingValidation')}</CardTitle>
                    <CardDescription>{translateStr('invoices.pendingValidationDescription')}</CardDescription>
                  </div>
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 bg-muted/50 rounded-md border p-3 px-4">
                  

                  <div className="flex flex-1 flex-wrap gap-2 w-full md:w-auto">
                    <div className="relative flex-1 min-w-[200px] md:w-56 md:flex-none">
                      <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="search"
                        placeholder={translateStr('students.search')}
                        className="pl-8"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon">
                          <Filter className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[260px]">
                        <DropdownMenuLabel>{translateStr('invoices.filters')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />

                        <div className="p-2 space-y-3">
                          <div>
                            <p className="text-xs mb-1 font-medium">{translateStr('invoices.student')}</p>
                            <Select value={selectedStudent?.id} onValueChange={(value) => {
                              const student = students.find((s: any) => String(s.id) === value);
                              setSelectedStudent(student);
                            }}>
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue placeholder={translateStr('invoices.filter.allStudents')} />
                              </SelectTrigger>
                              <SelectContent>
                                {students.map((student: any) => (
                                  <SelectItem key={student.id} value={String(student.id)}>
                                    {student.firstname} {student.lastname}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div>
                            <p className="text-xs mb-1 font-medium">{translateStr('invoices.filterByPeriod')}</p>
                            <Select value={filterPeriod} onValueChange={(value: FilterPeriod) => setFilterPeriod(value)}>
                              <SelectTrigger className="w-full h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">{translateStr('invoices.allTime')}</SelectItem>
                                <SelectItem value="currentMonth">{translateStr('invoices.currentMonth')}</SelectItem>
                                <SelectItem value="currentYear">{translateStr('invoices.currentYear')}</SelectItem>
                                <SelectItem value="custom">{translateStr('invoices.customDateRange')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {filterPeriod === 'custom' && (
                            <div>
                              <p className="text-xs mb-1 font-medium">{translateStr('invoices.dateRange')}</p>
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    className={cn(
                                      'w-full h-8 text-xs justify-start font-normal',
                                      !dateRange.from && 'text-muted-foreground'
                                    )}
                                    size="sm"
                                  >
                                    {dateRange.from ? (
                                      dateRange.to ? (
                                        <>
                                          {format(dateRange.from, 'PP')} - {format(dateRange.to, 'PP')}
                                        </>
                                      ) : (
                                        format(dateRange.from, 'PP')
                                      )
                                    ) : (
                                      <span>{translateStr('invoices.pickDateRange')}</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0" align="start">
                                  <CalendarComponent
                                    mode="range"
                                    selected={dateRange as RDPDateRange}
                                    onSelect={(range) => {
                                      setDateRange(range || {});
                                      setFilterPeriod('custom');
                                    }}
                                    numberOfMonths={2}
                                    initialFocus
                                    className={cn('p-3 pointer-events-auto')}
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}

                          <Button className="w-full" size="sm" variant="ghost" onClick={resetFilters}>
                            {translateStr('invoices.filter.resetFilters')}
                          </Button>
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex flex-wrap gap-2 shrink-0">
                    {selectedEventIds.size > 0 && (
                      <Button
                        onClick={() => handleValidate(Array.from(selectedEventIds))}
                        disabled={validatingIds.size > 0}
                        variant="outline"
                        className="w-full md:w-auto"
                      >
                        {validatingIds.size > 0 ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        {translateStr('invoices.validateSelected')} ({selectedEventIds.size})
                      </Button>
                    )}
                    {groupedEvents.length > 0 && (
                      <Button
                        onClick={() => setShowValidateAllConfirm(true)}
                        disabled={validatingIds.size > 0}
                        className="w-full md:w-auto"
                      >
                        {validatingIds.size > 0 && selectedEventIds.size === 0 ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 mr-2" />
                        )}
                        {translateStr('invoices.validateAll')} ({groupedEvents.length})
                      </Button>
                    )}
                  </div>

                 {/* <div className="text-sm text-muted-foreground">
                    {groupedEvents.length} {translateStr('invoices.results')}
                  </div>*/}
                </div>
                </div>
              </CardHeader>

              <CardContent className="p-0 md:p-4 mt-5 md:mt-0">
                {eventsLoading ? (
                  <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4">
                        <Skeleton className="h-12 w-12 rounded-full" />
                        <div className="space-y-2">
                          <Skeleton className="h-4 w-[250px]" />
                          <Skeleton className="h-4 w-[200px]" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : groupedEvents.length > 0 ? (
                  <>
                  <div className="md:hidden space-y-3">
                    {groupedEvents.map((event) => {
                      const isSelected = selectedEventIds.has(event.id);
                      const isValidating = validatingIds.has(event.id);
                      const isGroup = event.students.length > 1;

                      return (
                        <div key={event.id} className={cn('border rounded-md p-3', isSelected && 'bg-muted/50')}>
                          <div className="flex items-start gap-3">
                            <div className="pt-1">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleEvent(event.id)}
                                aria-label={translateStr('invoices.selectEvent')}
                              />
                            </div>
                            <div className="flex-1">
                              <div className="flex justify-between items-start">
                                <div>
                                  <div className="font-medium">
                                    <span className="leading-tight">
                                      {event.students[0]?.studentName}
                                    </span>
                                    {isGroup && (
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <button
                                            type="button"
                                            className="ml-1 inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-normal text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                                          >
                                            +{event.students.length - 1}
                                            <ChevronDown className="h-3 w-3" />
                                          </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto max-w-[260px] p-3 text-xs" align="start">
                                          <p className="font-medium mb-1">{translateStr('invoices.student')}s</p>
                                          <p className="text-muted-foreground">
                                            {event.students.map((s) => s.studentName).join(', ')}
                                          </p>
                                        </PopoverContent>
                                      </Popover>
                                    )}
                                  </div>
                                  <div className="text-xs text-muted-foreground mt-1">
                                    {formatDate(event.date)} • {event.allDay ? 'allDay' : `${event.duration} min`}
                                  </div>
                                </div>
                                <div className="text-right font-medium ml-4">
                                  {formatCurrency(event.totalPrice)}
                                </div>
                              </div>

                              <div className="mt-3">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleValidate([event.id])}
                                  disabled={isValidating || isSelected}
                                  className={cn('w-full', isSelected && 'opacity-50')}
                                >
                                  {isValidating ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      {isSelected ? (
                                        <Check className="h-3.5 w-3.5 mr-1" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5 mr-1" />
                                      )}
                                      {translateStr('invoices.validate')}
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="hidden md:block rounded-md border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40px]">
                            <Checkbox
                              checked={allSelected}
                              onCheckedChange={toggleAll}
                              aria-label={translateStr('invoices.selectAll')}
                            />
                          </TableHead>
                          <TableHead>{translateStr('invoices.student')}</TableHead>
                          <TableHead>{translateStr('invoices.date')}</TableHead>
                          <TableHead>{translateStr('invoices.category')}</TableHead>
                          <TableHead>{translateStr('invoices.duration')}</TableHead>
                          <TableHead className="text-right">{translateStr('invoices.amount')}</TableHead>
                          <TableHead className="w-[100px]"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {groupedEvents.map((event) => {
                          const isSelected = selectedEventIds.has(event.id);
                          const isValidating = validatingIds.has(event.id);
                          const isGroup = event.students.length > 1;

                          return (
                            <TableRow
                              key={event.id}
                              className={cn(isSelected && 'bg-muted/50')}
                            >
                              <TableCell>
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleEvent(event.id)}
                                  aria-label={translateStr('invoices.selectEvent')}
                                />
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {isGroup ? (
                                    <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                                  ) : (
                                    <User className="h-4 w-4 text-muted-foreground shrink-0" />
                                  )}
                                  <span className="font-medium leading-tight truncate">
                                    {event.students[0]?.studentName}
                                  </span>
                                  {isGroup && (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          className="inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 transition-colors shrink-0"
                                        >
                                          +{event.students.length - 1}
                                          <ChevronDown className="h-3 w-3" />
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto max-w-[280px] p-3 text-xs" align="start">
                                        <p className="font-medium mb-1">{translateStr('invoices.student')}s</p>
                                        <p className="text-muted-foreground">
                                          {event.students.map((s) => s.studentName).join(', ')}
                                        </p>
                                      </PopoverContent>
                                    </Popover>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <CalendarIcon className="h-3.5 w-3.5 text-muted-foreground" />
                                  {formatDate(event.date)}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div
                                    className="w-2.5 h-2.5 rounded-full"
                                    style={{ backgroundColor: event.categoryColor || '#3498db' }}
                                  />
                                  {event.category}
                                </div>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                                  {event.allDay ? 'allDay' : `${event.duration} min`}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                {formatCurrency(event.totalPrice)}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleValidate([event.id])}
                                  disabled={isValidating || isSelected}
                                  className={cn('w-full', isSelected && 'opacity-50')}
                                >
                                  {isValidating ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <>
                                      {isSelected ? (
                                        <Check className="h-3.5 w-3.5 mr-1" />
                                      ) : (
                                        <ChevronRight className="h-3.5 w-3.5 mr-1" />
                                      )}
                                      {translateStr('invoices.validate')}
                                    </>
                                  )}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  </>
                ) : (
                  <Alert>
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{translateStr('invoices.noPendingValidationEvents')}</AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </>
        ) : null}
      </main>

      <AlertDialog open={showValidateAllConfirm} onOpenChange={setShowValidateAllConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translateStr('invoices.validateAll')}</AlertDialogTitle>
            <AlertDialogDescription>
              <div className="mb-4">
                {groupedEvents.length}{' '}
              {groupedEvents.length === 1
                ? translateStr('invoices.pendingValidationEventSingular')
                : translateStr('invoices.pendingValidationEventPlural')}
              {validateAllDateRange && (
                <>
                  {' '}— {formatDate(validateAllDateRange.from.toISOString())} → {formatDate(validateAllDateRange.to.toISOString())}
                </>
              )}
              .
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{translateStr('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowValidateAllConfirm(false);
                handleValidate(groupedEvents.map((e) => e.id));
              }}
            >
              {translateStr('invoices.validate')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Footer />
    </div>
  );
};

export default InvoicesPendingValidation;
